/**
 * AttentionActionButtons (KAN-726, KAN-734)
 *
 * Buttons that close a row of the attention block. Each view of the block
 * passes the set of actions it offers.
 * KAN-757: `compact` draws icon-only buttons in one line (label in title and
 * aria-label) for the sticky actions column of the CRM table.
 * KAN-852: "done" is taking the client to work; its label comes from the
 * caller (useAttentionDone().label: "Взять в работу" or "Назначить в работу").
 * KAN-880: the outcomes "success" and "refused" are one button "Отработано"
 * with a choice "Успех" / "Не успех" (WorkOutcomeChoice); the choice calls the
 * same onAction('success' | 'refused'). "done" stays a separate button.
 * KAN-955: "Работа идёт" (work_continues) on rows with the idle reason; the
 * caller decides by the row (lib/workContinues).
 * KAN-965: `dense` for the row of the attention block: low buttons (h-8, small
 * text) in one row with the manager choice; the main actions keep the label,
 * the secondary "Работа идёт" is an icon (label in title and aria-label).
 */
import { Check, Hourglass } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TAKE_TO_WORK_LABEL } from '@/lib/workActions'
import { WORK_CONTINUES, WORK_CONTINUES_LABEL } from '@/lib/workContinues'
import type { AttentionAction } from './useAttentionActions'
import { WorkOutcomeChoice, type WorkOutcome } from './WorkOutcomeChoice'

interface AttentionActionButtonsProps {
  actions: readonly AttentionAction[]
  disabled: boolean
  onAction: (action: AttentionAction) => void
  compact?: boolean
  /** KAN-965: low buttons for the one-row actions of the attention row. */
  dense?: boolean
  /** KAN-852: label of the "done" button (by role); default "Взять в работу". */
  doneLabel?: string
}

const isOutcome = (action: AttentionAction): action is WorkOutcome =>
  action === 'success' || action === 'refused'

const ICON_BUTTON = { compact: 'h-7 w-7 p-0', dense: 'h-8 w-8 p-0' } as const
const DENSE_BUTTON = 'h-8 px-2 text-xs'

function wrapperClass(compact: boolean, dense: boolean): string {
  if (compact) return 'flex justify-end gap-1 flex-nowrap'
  if (dense) return 'flex flex-wrap items-center justify-end gap-1.5'
  return 'flex justify-end gap-1 flex-wrap'
}

export function AttentionActionButtons({
  actions,
  disabled,
  onAction,
  compact = false,
  dense = false,
  doneLabel,
}: AttentionActionButtonsProps) {
  const outcomes = actions.filter(isOutcome)
  const label = doneLabel ?? TAKE_TO_WORK_LABEL
  const isDense = dense && !compact
  // "Работа идёт" is an icon in the CRM table and in the attention row.
  const continuesIcon = compact || isDense
  return (
    <div className={wrapperClass(compact, isDense)}>
      {actions.includes('done') && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={compact ? ICON_BUTTON.compact : isDense ? DENSE_BUTTON : undefined}
          disabled={disabled}
          title={compact ? label : undefined}
          aria-label={compact ? label : undefined}
          onClick={() => onAction('done')}
        >
          <Check className={compact ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'} />
          {!compact && label}
        </Button>
      )}
      {actions.includes(WORK_CONTINUES) && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={compact ? ICON_BUTTON.compact : isDense ? ICON_BUTTON.dense : undefined}
          disabled={disabled}
          title={WORK_CONTINUES_LABEL}
          aria-label={continuesIcon ? WORK_CONTINUES_LABEL : undefined}
          data-testid="attention-work-continues"
          onClick={() => onAction(WORK_CONTINUES)}
        >
          <Hourglass className={continuesIcon ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'} />
          {!continuesIcon && WORK_CONTINUES_LABEL}
        </Button>
      )}
      {outcomes.length > 0 && (
        <WorkOutcomeChoice
          compact={compact}
          dense={isDense}
          disabled={disabled}
          outcomes={outcomes}
          onChoose={(outcome) => onAction(outcome)}
          className={isDense ? 'items-center justify-end' : 'justify-end'}
        />
      )}
    </div>
  )
}
